import { useEffect, useMemo, useRef } from 'react';

import { Toast } from '@/clientOnlyUtils/toast';
import { api } from '@/utils/api';

/**
 * Poll the async-enrichment indicator for the visible contacts
 * (docs/WATERFALL_ENRICHMENT.md §8): every ~5s WHILE at least one contact is
 * in flight, self-disarming when the set empties (same idiom as the
 * bad-numbers page's searching poll). Owns the completion side effects — the
 * enhance popup unmounts when its selection clears, so it cannot:
 *   - invalidate the contact views (and the credit counter, which now moves
 *     at completion, not click) when contacts LEAVE the in-flight set,
 *   - surface the explicit bulk skip toast ("N skipped — out of credits").
 */
export function useEnrichmentStatus(params: { userId: string; teamId: string; contactIds: string[] }) {
  const utils = api.useUtils();
  const query = api.contact.enrichmentStatus.useQuery(
    { userId: params.userId, teamId: params.teamId, contactIds: params.contactIds },
    {
      enabled: params.contactIds.length > 0,
      refetchInterval: (q) => (q.state.data?.statuses.some((s) => s.inFlight) ? 5000 : false),
    },
  );

  const statuses = query.data?.statuses;
  const prevInFlightRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (statuses == null) return;
    const nowInFlight = new Set(statuses.filter((s) => s.inFlight).map((s) => s.contactId));
    const finished = [...prevInFlightRef.current].filter((id) => !nowInFlight.has(id));
    prevInFlightRef.current = nowInFlight;
    if (finished.length === 0) return;

    // Fresh data landed server-side for these contacts.
    void utils.contact.search.invalidate();
    void utils.team.id.invalidate();
    for (const contactId of finished) void utils.contact.byId.invalidate({ contactId });

    // Batch done → surface skips explicitly (vendor-neutral copy).
    if (nowInFlight.size === 0) {
      const skipped = statuses.filter((s) => s.skipReason === 'out_of_credits').length;
      if (skipped > 0) {
        Toast.error({
          title: 'Enrichment finished',
          message: `${skipped} contact${skipped === 1 ? '' : 's'} skipped — out of credits`,
        });
      }
    }
  }, [statuses, utils]);

  const inFlightIds = useMemo(
    () => new Set((statuses ?? []).filter((s) => s.inFlight).map((s) => s.contactId)),
    [statuses],
  );
  return { inFlightIds };
}
